import { MachineApi } from './../api/machine.api';
import { Machine, MIFRequest } from './../utils/models/machine.model';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  BackendResponse,
  ModalService,
  StorageV2Service,
  ToastV2Service,
  TwoButtonModalV2Component,
} from '@schenck/rotec-components';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { TenantService } from './tenant.service';
import { BaseService } from './base.service';
import { SpinnerService } from './spinner-service/spinner.service';
import { first } from 'lodash';
import { MACHINE_GRID_DEFAULT_FILTERS } from 'src/utils/constants/machines.const';
import { TranslateService } from '@ngx-translate/core';
import { OVERWRITE_PASSWORDS_MODAL } from 'src/utils/constants/modals.const';

const serviceKey = 'Machine';

@Injectable()
export class MachineService extends BaseService<Machine> implements OnDestroy {
  protected machineApi: MachineApi;
  protected tenantService = inject(TenantService);
  protected toastService: ToastV2Service;
  protected spinnerService: SpinnerService;
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);

  protected filterMapFn = MACHINE_GRID_DEFAULT_FILTERS;
  private readonly onDestroy$ = new Subject<void>();

  constructor() {
    const storageService = inject(StorageV2Service);
    const machineApi = inject(MachineApi);
    const toastService = inject(ToastV2Service);
    const spinnerService = inject(SpinnerService);

    super(storageService, toastService, machineApi, serviceKey, spinnerService);
    this.machineApi = machineApi;
    this.toastService = toastService;
    this.spinnerService = spinnerService;

    this.initGridState();
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public subsribeToGridParamsChanges(): void {
    this._gridParams$
      .pipe(
        filter((params) => !!params),
        tap(() => this.setLoading(true)),
        switchMap(() => combineLatest([this.getMachines$(), this.tenantService.results$])),
        filter(([_, tenants]) => !!tenants),
        map(([machines, _]) => this.getMachinesWithTenantName(machines)),
        catchError(() => {
          this._results$.next([]);
          this.setLoading(false);

          return of([]);
        })
      )
      .subscribe((machines: Machine[]) => {
        this.setLoading(false);
        this._results$.next(machines);
        this.patchState('loaded machines grid results', this.getResults());
      });
  }

  public getMachineById$(id: string): Observable<Machine> {
    return this.machineApi.getMachineById$(id).pipe(
      filter((machine: Machine) => !!machine),
      map((machine: Machine) => first(this.getMachinesWithTenantName([machine])))
    );
  }

  public exportMachinesAsCSV(): void {
    this.handleCSVExport(this.machineApi.exportMachinesAsCSV$(), 'devices');
  }

  public purgeMessageQueue$(deviceId: string, purgeProgress$: BehaviorSubject<boolean>): void {
    purgeProgress$.next(true);

    this.machineApi
      .purgeMessageQueue$(deviceId)
      .pipe(
        catchError((err: string) => {
          this.toastService.open({
            type: 'error',
            text: `${this.translate.instant('An error occured:')} ${err}`,
          });
          throw Error(err);
        }),
        finalize(() => purgeProgress$.next(false))
      )
      .subscribe((result) => {
        this.toastService.open({
          type: 'success',
          text: this.translate.instant('NUMBER messages purged from device queue', {
            number: result.messagesPurged,
          }),
        });
      });
  }

  public sendPasswords$(deviceId: string, sendPasswordsProgress$: BehaviorSubject<boolean>): void {
    sendPasswordsProgress$.next(true);

    this.machineApi
      .getExistingPasswordFile$(deviceId)
      .pipe(
        switchMap((response) => {
          if (response.status === 200) {
            // Password exists, show prompt
            // Modal emits true on confirmation, false if canceled
            return this.modalService
              .open(TwoButtonModalV2Component, OVERWRITE_PASSWORDS_MODAL)
              .events$.pipe(
                switchMap((confirmed) => {
                  if (confirmed) {
                    return this.machineApi.sendPasswordFile$(deviceId);
                  } else {
                    return of(false);
                  }
                })
              );
          }
          if (response.status === 204) {
            // No password files exist, continue with the operation
            return this.machineApi.sendPasswordFile$(deviceId);
          }
          throw Error(`Unhandeled response status: ${response.status}`);
        }),
        catchError((err) => {
          if (err?.status === 400) {
            this.toastService.open({
              title: 'Error',
              type: 'error',
              text: this.translate.instant('Not all required passwords were set.'),
            });
          } else if (err?.status === 404) {
            this.toastService.open({
              title: 'Error',
              type: 'error',
              text: this.translate.instant('Device was not found.'),
            });
          } else {
            this.toastService.open({
              title: 'Error',
              type: 'error',
              text: `${this.translate.instant('An error occured:')} ${err?.message || err}`,
            });
          }
          sendPasswordsProgress$.next(false);
          throw Error(err);
        }),
        finalize(() => sendPasswordsProgress$.next(false))
      )
      .subscribe((response) => {
        if (response?.status) {
          this.toastService.open({
            title: 'Success',
            type: 'success',
            text: this.translate.instant('The Password file has been successfully created.'),
          });
        }
      });
  }

  public registerMIF$(data: MIFRequest): Observable<BackendResponse.Response<Machine>> {
    return this.machineApi.registerMIF$(data).pipe(takeUntil(this.onDestroy$));
  }

  private getMachines$(): Observable<Machine[]> {
    return this.machineApi.get$().pipe(share());
  }

  private getMachinesWithTenantName(machines: Machine[]): Machine[] {
    return machines.map((machine) => ({
      ...machine,
      tenantName: this.tenantService.getTenantNameById(machine?.tenantId),
    }));
  }
}
